// ============================================================
// HOOKED — CATCH CARD  (Phase 5 — the landing)
// The celebration. Provenance-stamped per the law: EARNED
// glows brighter than bought — and everything here is earned.
// Also renders the escape, the snap, and the Prophecy.
//
// v2 overhaul: the trophy finally shines. The landed card wears
// a per-Ilk HoloFoil (feTurbulence, unique per instance), the
// fish renders as designed FishArt, a light-burst fires on
// reveal, the grade sparkles, and the frame wears The Treatment.
// Grade/weight math untouched.
// ============================================================

import { useEffect } from "react";
import { GM, SD, WHARF_SLAB, facet, bevelPanel, textGlow, textEngrave, ilkAura } from "./theme";
import { Sprite } from "./Sprite";
import { getFishSprite } from "./SpriteManifest";
import { FishArt } from "./FishArt";
import { HoloOverlay } from "./HoloFoil";

export function gradeCatch(strikeGrade, accuracy) {
  const s = strikeGrade === "PERFECT" ? 1 : strikeGrade === "GOOD" ? 0.6 : 0.2;
  const score = s * 0.35 + accuracy * 0.65;
  if (score >= 0.9) return "FLAWLESS";
  if (score >= 0.7) return "CLEAN";
  if (score >= 0.45) return "HONEST";
  return "RAGGED";
}

export function rollWeight(fish, grade) {
  const base = fish.tier * 2.2 + Math.random() * fish.tier * 3;
  const mult = { FLAWLESS: 1.5, CLEAN: 1.2, HONEST: 1.0, RAGGED: 0.8 }[grade] ?? 1;
  return Math.round(base * mult * 10) / 10;
}

const GRADE_COLOR = {
  FLAWLESS: "#f5cd5e", CLEAN: "#35d3e0", HONEST: "#c3d2d6", RAGGED: "#9c8f74",
};

export function CatchCard({ result, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.key === "Enter" || e.key === "Escape") {
        e.preventDefault(); onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { kind, fish, grade, weight } = result;
  const landed = kind === "LANDED" || kind === "CURIO" || kind === "PROPHECY";
  const aura = fish ? (fish.curio ? SD.purpleLight : ilkAura(fish.ilk)) : GM.gold;

  const title =
    kind === "LANDED"   ? "LANDED" :
    kind === "PROPHECY" ? "THE PROPHECY" :
    kind === "CURIO"    ? "SOMETHING ELSE ENTIRELY" :
    kind === "SNAPPED"  ? "THE LINE GAVE" :
    kind === "SPOOKED"  ? "THE WATER WENT STILL" : "IT SLIPPED AWAY";

  const titleColor = landed ? GM.goldBright : kind === "SNAPPED" ? SD.redBright : SD.starDim;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 400,
        background: "radial-gradient(120% 100% at 50% 40%, rgba(3,8,22,0.7), rgba(1,2,8,0.92))",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", fontFamily: GM.fontDisplay,
        animation: "fadeUp 0.3s ease",
      }}>
      {landed && (
        <div style={{
          position: "absolute", left: "50%", top: "44%",
          width: 360, height: 360, borderRadius: "50%",
          background: `radial-gradient(circle, ${aura}44, transparent 65%)`,
          animation: "landBurst 0.9s ease-out forwards", pointerEvents: "none",
        }} />
      )}

      <div style={{
        position: "relative",
        width: "min(348px, 88vw)",
        background: WHARF_SLAB,
        clipPath: facet(16),
        padding: "28px 24px 22px",
        textAlign: "center",
        ...bevelPanel(landed ? aura : GM.inkBrown),
        animation: landed ? "catchGlow 1.6s ease-in-out infinite alternate" : "fadeUp 0.3s ease",
      }}>
        {landed && fish && !fish.curio && <HoloOverlay fish={fish} radius={0} />}

        <div style={{ position: "relative", zIndex: 2 }}>
          <div style={{ fontSize: 15, letterSpacing: 5, color: titleColor, ...textGlow(titleColor) }}>
            ◆ {title} ◆
          </div>

          {fish && (
            <>
              <div style={{ width: 132, height: 132, margin: "16px auto 8px", position: "relative" }}>
                {/* the breach spray — water thrown up as it clears the surface */}
                {landed && !fish.curio && (
                  <>
                    {Array.from({ length: 9 }).map((_, k) => (
                      <div key={`spray-${k}`} style={{
                        position: "absolute", left: "50%", top: "78%",
                        width: 3, height: 12 + (k % 3) * 5, borderRadius: 2,
                        background: SD.foam,
                        transform: `translate(-50%,-50%) rotate(${(k - 4) * 22}deg)`,
                        transformOrigin: "bottom center",
                        animation: `breachSpray 0.7s ease-out ${0.1 + k * 0.02}s both`,
                      }} />
                    ))}
                  </>
                )}
                <div style={landed && !fish.curio
                  ? { animation: "breach 0.8s cubic-bezier(.2,.7,.3,1) both" }
                  : {}}>
                  <Sprite
                    src={fish.curio ? null : getFishSprite(fish.id)}
                    size={132}
                    fallbackColor="transparent"
                    fallbackContent={
                      fish.curio
                        ? <span style={{ fontSize: 84, filter: `drop-shadow(0 0 16px ${aura})` }}>{fish.glyph}</span>
                        : <FishArt fish={fish} size={132} animate />
                    }
                  />
                </div>
                {landed && (grade === "FLAWLESS" || grade === "CLEAN") &&
                  Array.from({ length: 7 }).map((_, k) => (
                    <div key={k} style={{
                      position: "absolute", left: `${15 + (k * 47) % 80}%`, top: `${10 + (k * 31) % 78}%`,
                      width: 5, height: 5,
                      background: k % 2 ? GM.goldPale : aura,
                      clipPath: "polygon(50% 0,60% 40%,100% 50%,60% 60%,50% 100%,40% 60%,0 50%,40% 40%)",
                      boxShadow: `0 0 8px ${GM.goldBright}`,
                      animation: `sparkle ${1 + (k % 3) * 0.4}s ease-in-out ${k * 0.15}s infinite`,
                    }} />
                  ))}
              </div>
              <div style={{ fontSize: 20, color: GM.ink, fontWeight: 700, ...textEngrave() }}>{fish.name}</div>
              <div style={{ fontSize: 10, letterSpacing: 2.5, color: aura, marginTop: 4, ...textGlow(aura) }}>
                {fish.curio ? "CURIO" : `${fish.ilk.toUpperCase()} · ${"★".repeat(Math.min(fish.tier, 3))}${fish.legend ? " · LEGEND" : ""}`}
              </div>
              {fish.flavor && (
                <div style={{ fontSize: 12, color: GM.inkDim, fontStyle: "italic", marginTop: 8, fontFamily: GM.fontBody }}>
                  “{fish.flavor}”
                </div>
              )}
            </>
          )}

          {landed && grade && (
            <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 18, fontFamily: GM.fontUI,
              animation: "gradeReveal 0.5s ease 0.2s both" }}>
              <Stat label="GRADE" value={grade} color={GRADE_COLOR[grade] || GM.ink} />
              {weight != null && <Stat label="WEIGHT" value={`${weight} lb`} color={GM.ink} />}
              <Stat label="REPUTE" value={`+${fish?.renown ?? 0}`} color={GM.goldBright} />
            </div>
          )}

          {kind === "PROPHECY" && (
            <div style={{ fontSize: 11, color: SD.foam, marginTop: 12, fontStyle: "italic", fontFamily: GM.fontBody,
              animation: "prophecyWaver 3s ease-in-out infinite" }}>
              She achieved her ambition. Her scale is yours.
            </div>
          )}

          {!landed && kind !== "SPOOKED" && fish && (
            <div style={{ fontSize: 10, color: SD.purpleLight, marginTop: 12, letterSpacing: 1 }}>
              It sinks into the Murk. It will remember you — and you it.
            </div>
          )}

          {landed && (
            <div style={{
              marginTop: 16, display: "inline-block",
              fontSize: 8, letterSpacing: 3, color: GM.goldBright,
              padding: "4px 14px", borderRadius: 999,
              border: `1px solid ${GM.gold}55`, background: `${GM.gold}12`,
              ...textGlow(GM.gold),
            }}>
              ◆ EARNED ◆
            </div>
          )}

          <div style={{ fontSize: 9, color: GM.inkDim, letterSpacing: 1.5, marginTop: 16 }}>
            SPACE / TAP to return to the water
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 8, letterSpacing: 2, color: GM.inkDim }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 900, color, textShadow: `0 0 10px ${color}55` }}>{value}</div>
    </div>
  );
}
